// Liste des boards Kanban : création, duplication (template), suppression
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Copy, Trash2, SquareKanban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatJour } from "@/lib/dates";

export default function Boards() {
  const { user } = useAuth();
  const canEdit = ["admin", "media"].includes(user.role);
  const [boards, setBoards] = useState([]);
  const [dialogOuvert, setDialogOuvert] = useState(false);
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/boards");
      setBoards(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const creer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/boards", { nom, description });
      toast.success("Board créé");
      setDialogOuvert(false);
      setNom("");
      setDescription("");
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const dupliquer = async (id) => {
    try {
      await api.post(`/boards/${id}/duplicate`);
      toast.success("Board dupliqué");
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const supprimer = async (id) => {
    if (!window.confirm("Supprimer ce board et toutes ses cartes ?")) return;
    try {
      await api.delete(`/boards/${id}`);
      toast.success("Board supprimé");
      charger();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="boards-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold">Projets</h1>
          <p className="text-muted-foreground text-sm mt-1">Boards Kanban de l'équipe média</p>
        </div>
        {canEdit && (
          <Button onClick={() => setDialogOuvert(true)} className="rounded-full gap-1.5" data-testid="new-board-button">
            <Plus className="h-4 w-4" /> Nouveau board
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {boards.map((b) => (
          <div key={b.id} className="group bg-card rounded-2xl border border-border p-6 carte-douce apparition" data-testid={`board-card-${b.id}`}>
            <Link to={`/projets/${b.id}`} className="block">
              <div className="flex items-start justify-between">
                <SquareKanban className="h-8 w-8 text-primary" />
                <span className="text-xs text-muted-foreground rounded-full bg-muted px-2.5 py-1">{b.nb_cartes} carte{b.nb_cartes > 1 ? "s" : ""}</span>
              </div>
              <h3 className="font-serif text-2xl font-semibold mt-4 group-hover:text-primary transition-colors">{b.nom}</h3>
              {b.description && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{b.description}</p>}
              <p className="text-xs text-muted-foreground/70 mt-3">Créé le {formatJour(b.created_at)}</p>
            </Link>
            {canEdit && (
              <div className="flex gap-1 mt-4 pt-3 border-t border-border/60">
                <Button variant="ghost" size="sm" onClick={() => dupliquer(b.id)} className="gap-1.5 rounded-full text-muted-foreground" data-testid={`board-duplicate-${b.id}`}>
                  <Copy className="h-3.5 w-3.5" /> Dupliquer
                </Button>
                <Button variant="ghost" size="sm" onClick={() => supprimer(b.id)} className="gap-1.5 rounded-full text-destructive ml-auto" data-testid={`board-delete-${b.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        ))}
        {boards.length === 0 && (
          <p className="text-muted-foreground col-span-full text-center py-12">Aucun board pour l'instant.</p>
        )}
      </div>

      <Dialog open={dialogOuvert} onOpenChange={setDialogOuvert}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Nouveau board</DialogTitle>
            <DialogDescription>Un board regroupe les colonnes et les cartes d'un projet.</DialogDescription>
          </DialogHeader>
          <form onSubmit={creer} className="space-y-4">
            <div>
              <Label>Nom *</Label>
              <Input required value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Ex. Flash mob septembre" className="rounded-xl mt-1.5" data-testid="board-nom-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-xl mt-1.5" data-testid="board-description-input" />
            </div>
            <DialogFooter>
              <Button type="submit" className="rounded-full" data-testid="board-create-button">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
